<template>
    <button
        :type="nativeType"
        :class="btnClass"
        :disabled="disabled || loading"
        v-bind="$attrs"
    >
        <span v-if="loading" class="learn-u-btn__spinner" aria-hidden="true" />
        <slot v-else />
    </button>
</template>

<script>
/**
 * Drop-in replacement for Vuetify's `<LearnUBtn>` used by the Phase 2.3 codemod.
 * Mirrors the prop surface the Edaptio codebase actually exercises (see
 * `find src -name '*.vue' -exec grep -ho '<LearnUBtn[^>]*>' {} +`):
 *
 *   size    : small | large | x-small | x-large       (default: medium)
 *   shape   : icon | fab | tile                        (default: rounded)
 *   variant : flat | outline | outlined                (default: filled)
 *   color   : tailwind color token (accent, primary, fadeBlack, white, …)
 *   dark    : forces white text (Vuetify shorthand for `class="white--text"`)
 *   loading : disables + shows a spinner
 *   disabled: forwarded
 *   block   : tw-w-full
 *
 * Listeners ($listeners) are forwarded so `@click`, `@click` (Vue 2),
 * `@mousedown` etc. all still work without changes at the call site.
 *
 * Vuetify shorthand classes (`accent white--text`, `primary white--text`) are
 * NOT translated automatically — the codemod converts the most common
 * patterns inline; anything left over still works because Tailwind has the
 * matching `tw-bg-accent` / `tw-text-white` utilities defined in
 * tailwind.config.js. A small `<style>` block below provides the
 * Vuetify-compat color short-classes (`accent`, `primary`, `white--text`, …)
 * as scoped fallbacks so legacy class strings keep rendering.
 */
export default {
    name: 'LearnUBtn',
    inheritAttrs: false, // class merged manually so we control ordering
    props: {
        small: { type: Boolean, default: false },
        xSmall: { type: Boolean, default: false },
        large: { type: Boolean, default: false },
        xLarge: { type: Boolean, default: false },
        icon: { type: Boolean, default: false },
        fab: { type: Boolean, default: false },
        tile: { type: Boolean, default: false },
        flat: { type: Boolean, default: false },
        outline: { type: Boolean, default: false },
        outlined: { type: Boolean, default: false },
        block: { type: Boolean, default: false },
        dark: { type: Boolean, default: false },
        loading: { type: Boolean, default: false },
        disabled: { type: Boolean, default: false },
        color: { type: String, default: '' },
        nativeType: { type: String, default: 'button' },
    },
    computed: {
        btnClass() {
            const cls = [
                'learn-u-btn',
                'tw-inline-flex',
                'tw-items-center',
                'tw-justify-center',
                'tw-font-bodySemibold',
                'tw-transition-colors',
            ]

            // Shape ---------------------------------------------------------
            if (this.icon) {
                cls.push('tw-rounded-full', 'tw-w-9', 'tw-h-9', 'tw-p-0', 'tw-bg-transparent')
            } else if (this.fab) {
                cls.push('tw-rounded-full', 'tw-w-13', 'tw-h-13', 'tw-shadow-card')
            } else if (this.tile) {
                cls.push('tw-rounded-none')
            } else {
                cls.push('tw-rounded-s')
            }

            // Size ----------------------------------------------------------
            if (!this.icon && !this.fab) {
                if (this.xSmall) cls.push('tw-px-2', 'tw-py-1', 'tw-text-xs')
                else if (this.small) cls.push('tw-px-3', 'tw-py-1.5', 'tw-text-sm')
                else if (this.large) cls.push('tw-px-6', 'tw-py-3', 'tw-text-lg')
                else if (this.xLarge) cls.push('tw-px-8', 'tw-py-4', 'tw-text-xl')
                else cls.push('tw-px-4', 'tw-py-2', 'tw-text-base')
            }

            // Variant -------------------------------------------------------
            if (this.flat) {
                cls.push('tw-bg-transparent', 'tw-shadow-none')
            } else if (this.outline || this.outlined) {
                cls.push('tw-bg-transparent', 'tw-border')
                if (this.color) cls.push(`tw-border-${this.color}`, `tw-text-${this.color}`)
                else cls.push('tw-border-gray', 'tw-text-gray')
            } else if (!this.icon) {
                if (this.color) cls.push(`tw-bg-${this.color}`)
                if (this.dark) cls.push('tw-text-white')
            }

            // Block (full-width) -------------------------------------------
            if (this.block) cls.push('tw-w-full')

            // Disabled / loading -------------------------------------------
            if (this.disabled || this.loading) cls.push('tw-opacity-50', 'tw-cursor-not-allowed')
            else cls.push('hover:tw-opacity-90')

            // User-supplied class= survives via $attrs.class (kept after ours)
            if (this.$attrs.class) cls.push(this.$attrs.class)
            return cls
        },
    },
}
</script>

<style scoped>
.learn-u-btn__spinner {
    display: inline-block;
    width: 1em;
    height: 1em;
    border: 2px solid currentColor;
    border-right-color: transparent;
    border-radius: 50%;
    animation: learn-u-btn-spin 0.7s linear infinite;
}
@keyframes learn-u-btn-spin {
    to { transform: rotate(360deg); }
}
</style>
